/**
 * Service for processing Game Boy ROM files
 * Based on the original Java implementation in Document.java and RomUtilities.java
 */

import {BinaryProcessor} from './BinaryProcessor';

/**
 * Constants for ROM file processing
 */
export const ROM_CONSTANTS = {
  BANK_COUNT: 64,
  BANK_SIZE: 0x4000, // 16,384 bytes
  COLOR_SET_SIZE: 4 * 2, // one color set contains 4 colors
  NUM_COLOR_SETS: 5, // one palette contains 5 color sets
  PALETTE_SIZE: 4 * 2 * 5, // COLOR_SET_SIZE * NUM_COLOR_SETS
  PALETTE_NAME_SIZE: 5,
};

/**
 * Interface for ROM file information
 */
export interface RomInfo {
  title: string;
  version: string;
  isValid: boolean;
  size: number;
  banks: number;
  hasPalettes: boolean;
  hasFonts: boolean;
}

/**
 * Service for processing Game Boy ROM files
 */
export const RomProcessor = {
  /**
   * Parse a ROM file and extract basic information
   * 
   * @param romData - The ROM file data as an ArrayBuffer
   * @returns Information about the ROM file
   */
  parseRom(romData: ArrayBuffer): RomInfo {
    const processor = new BinaryProcessor(romData);
    const size = processor.bufferSize;

    // Check if the ROM size is valid (must be a multiple of BANK_SIZE)
    const isValidSize = size > 0 && size % ROM_CONSTANTS.BANK_SIZE === 0;
    const banks = isValidSize ? size / ROM_CONSTANTS.BANK_SIZE : 0;

    // Read the ROM title from the header (located at 0x134-0x143)
    const title = processor.readAsciiString(0x134, 16).trim();

    // Read the ROM version (located at 0x14C)
    const versionByte = processor.readUint8(0x14C);
    const version = `v${versionByte.toString(16).padStart(2, '0')}`;

    // Check if the ROM has palettes and fonts
    const hasPalettes = banks >= 2 && this.validatePaletteData(processor);

    // Check if the ROM has fonts (requires at least 31 banks)
    const hasFonts = banks >= 31 && this.findFontOffset(processor) !== -1;

    return {
      title,
      version,
      isValid: isValidSize,
      size,
      banks,
      hasPalettes,
      hasFonts,
    };
  },

  /**
   * Validate palette data in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns Whether the ROM has valid palette data
   */
  validatePaletteData(processor: BinaryProcessor): boolean {
    try {
      return (
        this.getNumberOfPalettes(processor) > 0 &&
        this.findPaletteNameOffset(processor) > 0 &&
        this.findPaletteOffset(processor) > 0
      );
    } catch (error) {
      // If we encounter any errors, assume the ROM doesn't have valid palette data
      console.warn('Error validating palette data:', error);
      return false;
    }
  },

  /**
   * Find the grayscale palette names in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the grayscale palette names, or -1 if not found
   */
  findGrayscalePaletteNames(processor: BinaryProcessor): number {
    // Palette names are in bank 27
    const bankStart = 27 * ROM_CONSTANTS.BANK_SIZE;
    const bankEnd = 28 * ROM_CONSTANTS.BANK_SIZE;

    // Check if the ROM is large enough to contain bank 27
    if (processor.bufferSize < bankEnd) {
      return -1;
    }

    try {
      for (let i = bankStart; i < bankEnd; i++) {
        // Make sure we don't read beyond the buffer
        if (i + 14 >= processor.bufferSize) {
          break;
        }

        // Check for the pattern of non-zero bytes followed by zero bytes
        // that identifies the grayscale palette names
        if (
          processor.readUint8(i) !== 0 &&
          processor.readUint8(i + 1) !== 0 &&
          processor.readUint8(i + 2) !== 0 &&
          processor.readUint8(i + 3) !== 0 &&
          processor.readUint8(i + 4) === 0 &&
          processor.readUint8(i + 5) !== 0 &&
          processor.readUint8(i + 6) !== 0 &&
          processor.readUint8(i + 7) !== 0 &&
          processor.readUint8(i + 8) !== 0 &&
          processor.readUint8(i + 9) === 0 &&
          processor.readUint8(i + 10) !== 0 &&
          processor.readUint8(i + 11) !== 0 &&
          processor.readUint8(i + 12) !== 0 &&
          processor.readUint8(i + 13) !== 0 &&
          processor.readUint8(i + 14) === 0
        ) {
          return i + 15;
        }
      }
    } catch (error) {
      console.warn('Error finding grayscale palette names:', error);
    }

    return -1;
  },

  /**
   * Get the number of palettes in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The number of palettes, or -1 if not found
   */
  getNumberOfPalettes(processor: BinaryProcessor): number {
    const baseOffset = this.findGrayscalePaletteNames(processor);
    if (baseOffset === -1) {
      return -1;
    }

    let numPalettes = 0;
    try {
      // Add a safety limit to prevent infinite loops or buffer overruns
      const maxPalettes = 20; // Reasonable upper limit
      for (let j = baseOffset + 4; 
           j < processor.bufferSize && 
           numPalettes < maxPalettes && 
           processor.readUint8(j) === 0; 
           j += 5) {
        numPalettes++;
      }
    } catch (error) {
      // If we encounter an error (like buffer access out of bounds),
      // just return what we've counted so far
      console.warn('Error counting palettes:', error);
    }

    // If we found at least one palette, consider it valid
    if (numPalettes > 0) {
      return Math.floor(numPalettes / 2);
    }

    return -1;
  },

  /**
   * Find the palette name offset in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the palette names, or -1 if not found
   */
  findPaletteNameOffset(processor: BinaryProcessor): number {
    const baseOffset = this.findGrayscalePaletteNames(processor);
    if (baseOffset === -1) {
      return -1;
    }

    return baseOffset + 5 * this.getNumberOfPalettes(processor);
  },

  /**
   * Find the screen background data in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the screen background data, or -1 if not found
   */
  findScreenBackgroundData(processor: BinaryProcessor): number {
    const numPalettes = this.getNumberOfPalettes(processor);
    if (numPalettes === -1) {
      return -1;
    }

    // Screen background data is in bank 1
    const bankStart = ROM_CONSTANTS.BANK_SIZE;
    const bankEnd = 2 * ROM_CONSTANTS.BANK_SIZE;

    for (let i = bankStart; i < bankEnd; i++) {
      // Check for the pattern of zero bytes followed by specific values
      // that identifies the screen background data
      if (
        processor.readUint8(i) === 0 &&
        processor.readUint8(i + 1) === 0 &&
        processor.readUint8(i + 2) === 0 &&
        processor.readUint8(i + 3) === 0 &&
        processor.readUint8(i + 4) === 0 &&
        processor.readUint8(i + 5) === 0 &&
        processor.readUint8(i + 6) === 0 &&
        processor.readUint8(i + 7) === 0 &&
        processor.readUint8(i + 8) === 0 &&
        processor.readUint8(i + 9) === 0 &&
        processor.readUint8(i + 10) === 0 &&
        processor.readUint8(i + 11) === 0 &&
        processor.readUint8(i + 12) === 0 &&
        processor.readUint8(i + 13) === 0 &&
        processor.readUint8(i + 14) === 0 &&
        processor.readUint8(i + 15) === 0 &&
        processor.readUint8(i + 16) === 0 &&
        processor.readUint8(i + 17) === 72 &&
        processor.readUint8(i + 18) === 72 &&
        processor.readUint8(i + 19) === 72
      ) {
        return i;
      }
    }

    return -1;
  },

  /**
   * Find the palette offset in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the palettes, or -1 if not found
   */
  findPaletteOffset(processor: BinaryProcessor): number {
    const baseOffset = this.findScreenBackgroundData(processor);
    if (baseOffset === -1) {
      return -1;
    }

    return baseOffset - this.getNumberOfPalettes(processor) * ROM_CONSTANTS.PALETTE_SIZE;
  },

  /**
   * Find the font offset in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the fonts, or -1 if not found
   */
  findFontOffset(processor: BinaryProcessor): number {
    const gfxOffset = this.findGfxFontOffset(processor);
    const gfxCharacterCount = 46;
    const gfxCharacterSize = 16;

    return gfxOffset === -1 ? -1 : gfxOffset + gfxCharacterCount * gfxCharacterSize;
  },

  /**
   * Find the graphics font offset in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the graphics font, or -1 if not found
   */
  findGfxFontOffset(processor: BinaryProcessor): number {
    // Graphics font is in bank 30
    const bankStart = 30 * ROM_CONSTANTS.BANK_SIZE;
    const bankEnd = 31 * ROM_CONSTANTS.BANK_SIZE;

    for (let i = bankStart; i < bankEnd; i++) {
      // Check for the pattern that identifies the graphics font
      if (
        processor.readUint8(i) === 1 &&
        processor.readUint8(i + 1) === 46 &&
        processor.readUint8(i + 2) === 0 &&
        processor.readUint8(i + 3) === 1
      ) {
        return i + 2 + 8 * 16;
      }
    }

    return -1;
  },

  /**
   * Find the font name offset in the ROM
   * 
   * @param processor - The BinaryProcessor containing the ROM data
   * @returns The offset of the font names, or -1 if not found
   */
  findFontNameOffset(processor: BinaryProcessor): number {
    // Font names are in bank 27, just before the grayscale palette names
    const baseOffset = this.findGrayscalePaletteNames(processor);
    if (baseOffset === -1) {
      return -1;
    }
    return baseOffset - 15;
  },

  /**
   * Fix the checksums in the ROM header
   * This is required for the ROM to be valid and work on real hardware or emulators
   * Based on the original Java implementation in RomUtilities.java
   * 
   * @param romData - The ROM data as an ArrayBuffer
   * @returns The ROM data with fixed checksums
   */
  fixChecksum(romData: ArrayBuffer): ArrayBuffer {
    // Create a copy of the ROM data to avoid mutating the original
    const romCopy = new ArrayBuffer(romData.byteLength);
    new Uint8Array(romCopy).set(new Uint8Array(romData));

    const romView = new Uint8Array(romCopy);

    // Calculate the header checksum (0x14D)
    let checksum014D = 0;
    for (let i = 0x134; i < 0x14D; ++i) {
      checksum014D = checksum014D - romView[i] - 1;
    }
    romView[0x14D] = checksum014D & 0xFF;

    // Calculate the global checksum (0x14E-0x14F)
    let checksum014E = 0;
    for (let i = 0; i < romView.length; ++i) {
      if (i === 0x14E || i === 0x14F) {
        continue;
      }
      checksum014E += romView[i] & 0xFF;
    }

    romView[0x14E] = (checksum014E & 0xFF00) >> 8;
    romView[0x14F] = checksum014E & 0x00FF;

    return romCopy;
  },
};
